import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import FadeIn from "../components/FadeIn";
import SEO from "../components/SEO";
import MatchingNetworkSection from "../components/MatchingNetworkSection";
import DatingStatsSection from "../components/DatingStatsSection";
import HowPeopleUseHaevn from "../components/HowPeopleUseHaevn";
import HowItWorksTiles from "../components/HowItWorksTiles";

const SURVEY_URL = process.env.REACT_APP_SURVEY_URL;

const SectionDivider = () => (
  <div className="px-6 md:px-12">
    <div className="max-w-5xl mx-auto py-16">
      <div className="h-px bg-[#E5E7EB] w-full" />
    </div>
  </div>
);

const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_2ce4513b-5c90-4107-90a4-2290c44a379e/artifacts/gt03zziv_IMG_1287.jpeg",
  heroMobile: "https://customer-assets.emergentagent.com/job_2ce4513b-5c90-4107-90a4-2290c44a379e/artifacts/gt03zziv_IMG_1287.jpeg",
};

export default function HomePage() {
  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <SEO
        path="/"
        description="HAEVN is a matching network that connects singles based on 80%+ compatibility. No swiping. No searching. Just qualified matches."
      />
      <Navigation />

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.heroMobile} alt="" className="w-full h-full object-cover object-[60%_30%] md:hidden scale-110" />
          <img src={IMAGES.hero} alt="" className="w-full h-full object-cover object-[60%_center] hidden md:block" />
          <div className="absolute inset-0 bg-gradient-to-r from-[rgba(10,15,25,0.75)] via-[rgba(10,15,25,0.4)] to-[rgba(10,15,25,0.05)]" />
          <div className="absolute inset-0 bg-gradient-to-b from-[rgba(10,15,25,0.3)] to-transparent" style={{backgroundSize: "100% 40%", backgroundRepeat: "no-repeat"}} />
        </div>
        <div className="relative z-10 px-6 md:px-12 min-h-[720px] md:min-h-[520px] flex flex-col md:justify-start md:pt-24">
          <div className="max-w-[320px] md:max-w-4xl md:mx-auto md:w-full flex flex-col pb-20 md:pb-0 md:flex-1 md:flex pt-[35vh] md:pt-0">
            <div>
              <FadeIn>
                <h1 className="font-headline text-[4rem] sm:text-6xl lg:text-7xl text-white" style={{lineHeight: "1.05"}}>
                  <span className="md:hidden">Still swiping<br />to find love?</span>
                  <span className="hidden md:inline">Still swiping to find love?</span>
                  <span className="hidden md:block mt-0 text-white/80" style={{fontWeight: 300}}>That's cute.</span>
                </h1>
              </FadeIn>
              <FadeIn delay={100}>
                <p className="font-headline text-[2.6rem] text-white/80 mt-1 md:hidden" style={{fontWeight: 300, lineHeight: 1.1}}>That's cute.</p>
                <p className="text-xl md:text-3xl text-white/75 font-headline max-w-xl mt-3 md:mt-0" style={{fontWeight: 400}}>
                  There's a better way. Welcome to HAEVN.
                </p>
              </FadeIn>
            </div>
            <div className="hidden md:block flex-1 min-h-[40px]" />
            <FadeIn delay={300}>
              <div className="flex flex-col sm:flex-row gap-4 mt-5 md:mt-0 pb-0 md:pb-14 max-w-[280px] md:max-w-none">
                <a href={SURVEY_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-haevn-gold text-white px-8 py-4 font-subhead text-base hover:bg-haevn-gold/90 transition-all duration-200 group" data-testid="hero-cta-primary">
                  Start Your Survey
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </a>
                <a href="#clarity" className="inline-flex items-center justify-center gap-2 border border-white/40 text-white/70 px-6 py-3 font-body text-sm hover:border-white hover:text-white transition-all duration-200 group" data-testid="hero-cta-secondary">
                  What is HAEVN?
                  <ArrowDown className="w-5 h-5 transition-transform group-hover:translate-y-0.5" />
                </a>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* As Seen In */}
      <section className="py-6 px-6 md:px-12 bg-white" data-testid="press-section">
        <div className="max-w-3xl mx-auto flex items-center justify-center gap-10 md:gap-16">
          <span className="text-[10px] font-subhead uppercase tracking-widest text-gray-400 flex-shrink-0">As seen in</span>
          <img src="https://customer-assets.emergentagent.com/job_7135488c-395e-477c-9ceb-46a81f357cdf/artifacts/je2k4jsa_Associated-Press-logo.png" alt="Associated Press" className="h-10 md:h-12 object-contain grayscale opacity-25" />
          <img src="https://customer-assets.emergentagent.com/job_7135488c-395e-477c-9ceb-46a81f357cdf/artifacts/5zi03629_MarketWatch-Logo-768x432.png" alt="MarketWatch" className="h-10 md:h-12 object-contain" style={{filter: "invert(1) brightness(0)", opacity: 0.25}} />
          <img src="https://customer-assets.emergentagent.com/job_7135488c-395e-477c-9ceb-46a81f357cdf/artifacts/0crqli8v_Yahoo-logo-768x432.png" alt="Yahoo" className="h-10 md:h-12 object-contain" style={{filter: "invert(1) brightness(0)", opacity: 0.25}} />
        </div>
      </section>

      <div className="px-6 md:px-12 py-2">
        <div className="max-w-5xl mx-auto">
          <div className="h-px bg-[#E5E7EB] w-full" />
        </div>
      </div>

      <MatchingNetworkSection />

      {/* How It Works */}
      <section className="px-6 md:px-12 py-16 md:py-20 bg-[#F3F2F0]" data-testid="how-it-works-micro">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <p className="text-xs font-subhead uppercase tracking-widest text-gray-400 mb-4">How it works</p>
            <h2 className="font-headline text-3xl md:text-4xl text-haevn-navy leading-tight mb-12">
              Matching, Not Searching.
            </h2>
          </FadeIn>
          <div className="space-y-12 md:space-y-16 mb-14">
            <FadeIn delay={100}>
              <div className="flex items-start gap-6">
                <span className="text-5xl md:text-6xl font-headline text-haevn-teal/40 leading-none shrink-0">01</span>
                <div>
                  <h3 className="font-subhead text-haevn-navy text-lg md:text-xl mb-2">Complete a short survey</h3>
                  <p className="text-base text-gray-500 font-body leading-relaxed">You join by telling us who you are and what you're looking for. Takes about 10 minutes.</p>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={200}>
              <div className="flex items-start gap-6">
                <span className="text-5xl md:text-6xl font-headline text-haevn-teal/40 leading-none shrink-0">02</span>
                <div>
                  <h3 className="font-subhead text-haevn-navy text-lg md:text-xl mb-2">The system searches for you</h3>
                  <p className="text-base text-gray-500 font-body leading-relaxed">HAEVN continuously looks for people who match what you're looking for. You don't have to do anything.</p>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={300}>
              <div className="flex items-start gap-6">
                <span className="text-5xl md:text-6xl font-headline text-haevn-teal/40 leading-none shrink-0">03</span>
                <div>
                  <h3 className="font-subhead text-haevn-navy text-lg md:text-xl mb-2">Both of you get notified</h3>
                  <p className="text-base text-gray-500 font-body leading-relaxed">When a match is found, both people are notified at the same time. No one is left waiting.</p>
                </div>
              </div>
            </FadeIn>
          </div>
          <FadeIn delay={400}>
            <div className="pt-2">
              <p className="text-lg text-gray-500 font-body leading-relaxed italic mb-6">You'll only see people looking for the same kind of connection you are.</p>
              <Link to="/how-it-works" className="inline-flex items-center gap-2 text-sm font-subhead text-haevn-teal hover:text-haevn-navy transition-colors duration-200 group" data-testid="how-it-works-learn-more">
                Learn More
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      <HowPeopleUseHaevn />
      <SectionDivider />

      {/* Active Cities */}
      <section className="px-6 md:px-12 pb-16 bg-white" data-testid="cities-section">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-start">
            <div>
              <FadeIn>
                <p className="text-xs font-subhead uppercase tracking-widest text-gray-400 mb-4">HAEVN Network Cities</p>
                <h2 className="font-headline text-3xl md:text-4xl text-haevn-navy mb-6 leading-tight">We build each city before we turn it on.</h2>
              </FadeIn>
              <FadeIn delay={100}>
                <p className="text-base text-gray-600 font-body leading-relaxed">Thousands join. Matches build quietly. Then everything unlocks on Match Monday.</p>
              </FadeIn>
              <FadeIn delay={200}>
                <Link to="/cities" className="inline-flex items-center gap-2 text-sm font-subhead text-haevn-teal hover:text-haevn-navy transition-colors duration-200 group mt-8" data-testid="view-cities-link">
                  View all cities
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                </Link>
              </FadeIn>
            </div>
            <div>
              <FadeIn delay={200}>
                <p className="text-xs font-subhead uppercase tracking-widest text-gray-400 mb-6">Next to launch</p>
                <div className="space-y-5">
                  {[
                    { city: "Austin, TX", date: "June 2026", singles: "~1.2M singles", slug: "austin-tx", preLaunch: false },
                    { city: "Portland, OR", date: "August 2026", singles: "~1.1M singles", slug: "portland-or", preLaunch: true },
                    { city: "Tampa / St. Pete, FL", date: "October 2026", singles: "~1.6M singles", slug: "tampa-st-pete-fl", preLaunch: true },
                  ].map((c, i) => (
                    <div key={i} className="border-t border-gray-200 pt-4">
                      {c.preLaunch ? (
                        <Link to={`/waitlist/${c.slug}`} className="font-subhead text-haevn-navy text-base mb-1 hover:text-haevn-teal transition-colors duration-200 block">{c.city}</Link>
                      ) : (
                        <p className="font-subhead text-haevn-navy text-base mb-1">{c.city}</p>
                      )}
                      <p className="text-sm text-gray-500 font-body">
                        <span className="text-haevn-teal font-subhead">{c.date}</span>
                        <span className="mx-2 text-gray-300">|</span>
                        {c.singles}
                      </p>
                    </div>
                  ))}
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 md:px-12 bg-[#F3F2F0]" data-testid="positioning-section">
        <div className="max-w-5xl mx-auto text-center">
          <FadeIn>
            <h2 className="font-headline text-3xl md:text-4xl text-haevn-navy mb-4 leading-tight">Stop searching.</h2>
            <h2 className="font-headline text-3xl md:text-4xl text-haevn-navy mb-10 leading-tight">Start being found.</h2>
          </FadeIn>
          <FadeIn delay={100}>
            <a href={SURVEY_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-haevn-gold text-white px-10 py-4 font-subhead text-lg hover:bg-haevn-gold/90 transition-all duration-200 group" data-testid="final-cta-btn">
              Enter the Network
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </a>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
